import { showToastMessage } from "@almafintech/react-components";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { AppDispatch } from "../../app/store";
import { acceptRiskProfile } from "../../connectors/interactiveBrokers";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
} from "../../reducers/buttonsReducer";
import {
  onUpdateCompletedForm,
  onUpdateCompletedStep,
  onUpdateStep,
  onUpdateSubStep,
} from "../../reducers/onboardingDataReducer";
import { RiskProfile } from "../../types/interactiveBrokers";
import { genericToastifyError } from "../../utils";
import RiskProfileTable from "../InteractiveBrokersRiskProfileTable/RiskProfileTable";
import { texts } from "./i18n";
import styles from "./RiskProfileFeedback.module.scss";

const { riskProfileFeedback, riskProfileName } = styles;

interface Props {
  returnSubstep: number;
  dispatch: AppDispatch;
  language: "es" | "en";
  riskProfile: RiskProfile | null;
}

const RiskProfileFeedback = ({
  returnSubstep,
  riskProfile,
  language,
  dispatch,
}: Props) => {
  const [loading, setLoading] = useState(false);

  const handleAcceptClicked = async () => {
    try {
      setLoading(true);
      if (riskProfile?.id === undefined) throw new Error();

      await acceptRiskProfile({
        id: riskProfile.id,
        accept: true,
      });

      dispatch(onUpdateCompletedStep(3));
      dispatch(onUpdateStep(4));
      dispatch(onUpdateCompletedForm(true));
    } catch (error) {
      console.error("Error on step three profile acceptance:", error);
      Sentry.captureException(error);
      showToastMessage(genericToastifyError, {
        containerId: "formAlert",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackClicked = () => {
    dispatch(onUpdateSubStep(returnSubstep));
  };

  useEffect(() => {
    dispatch(
      onUpdatePrimaryButton({
        onClick: handleAcceptClicked,
        text: texts[language].primaryButton,
        type: "button",
        disabled: loading,
        isLoading: loading,
        show: true,
      })
    );

    dispatch(
      onUpdateSecondaryButton({
        onClick: handleBackClicked,
        text: texts[language].secondaryButton,
        disabled: false,
        isLoading: false,
        show: true,
      })
    );
  }, [loading]);

  return (
    <div className={`flex flex-col gap-4 ${riskProfileFeedback}`}>
      <div className={`w-full flex gap-4 justify-between items-center`}>
        <div className={`flex flex-col gap-2`}>
          <span className="font-semibold text-lg">{texts[language].title}</span>
          <span className="font-normal">{texts[language].subtitle}</span>
        </div>

        <span className={riskProfileName}>{riskProfile?.description}</span>
      </div>

      <hr />

      <div className={`flex flex-col gap-2`}>
        <span className="font-medium">{texts[language].tableTitle}</span>
        <RiskProfileTable />
      </div>
    </div>
  );
};

export default RiskProfileFeedback;
